// useEmpDetails.ts
import { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

export type EmployeeDetails = {
  employee_id: string;
  firstname: string;
  lastname: string;
  username: string;
  email: string;
  phone: string;
  role_name: string;
  join_date: string;
  company: string;
  department_name: string;
  designation_name: string;
  about: string;
  passport_no: string;
  passport_exp_date: string;
  nationality: string;
  marital_status: string;
  religion: string;
  spouse_name: string;
  number_of_children: number;
  emergency_contact_1: string;
  emergency_contact_2: string;
  aadhar_no: string;
  pan_card: string;
  employee_status: string;
  blood_group: string;
  photo: string | File;
  gender: string;
  date_of_birth: string;
  street_address: string;
  area: string;
  city_name: string;
  country: string;
  zip_code: string;
  state_name: string;
  overall_work_exp: number;
  job_type_name: string;
  earned_leave_total: number;
  role_data: { role_name: string };
  designation_data: { designation_name: string };
  department_data: { department_name: string };
};

const BASE_URL = API_BASE_PATH;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

const useEmpDetails = (token?: string | null) => {
  const [data, setData] = useState<EmployeeDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const fetchDetails = useCallback(
    async (employee_id: string, _token?: string | null) => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/register/admin/${employee_id}`,
          {
            headers: {
              ...authHeader,
              "Content-Type": "application/json",
            },
          }
        );

        const item = response.data;

        const transformed: EmployeeDetails = {
          employee_id: item.employee_id || "",
          firstname: item.firstname || "",
          lastname: item.lastname || "",
          username: item.username || "",
          email: item.email || "",
          phone: item.phone || "",
          role_name: item.role_name || "",
          join_date: item.join_date || "",
          company: item.company || "",
          department_name: item.department_name || "",
          designation_name: item.designation_name || "",
          about: item.about || "",
          passport_no: item.passport_no || "",
          passport_exp_date: item.passport_exp_date || "",
          nationality: item.nationality || "",
          marital_status: item.marital_status || "",
          religion: item.religion || "",
          spouse_name: item.spouse_name || "",
          number_of_children: item.number_of_children || 0,
          emergency_contact_1: item.emergency_contact_1?.toString() || "",
          emergency_contact_2: item.emergency_contact_2?.toString() || "",
          aadhar_no: item.aadhar_no?.toString() || "",
          pan_card: item.pan_card || "",
          employee_status: item.employee_status || "",
          blood_group: item.blood_group || "",
          photo: item.photo || "",
          gender: item.gender || "",
          date_of_birth: item.date_of_birth || "",
          street_address: item.street_address || "",
          area: item.area || "",
          city_name: item.city_name || "",
          country: item.country || "",
          zip_code: item.zip_code || "",
          state_name: item.state_name || "",
          overall_work_exp: item.overall_work_exp || 0,
          job_type_name: item.job_type_name || "",
          earned_leave_total: item.earned_leave_total || 0,
          role_data: {
            role_name: item.role_data?.role_name || "",
          },
          designation_data: {
            designation_name: item.designation_data?.designation_name || "",
          },
          department_data: {
            department_name: item.department_data?.department_name || "",
          },
        };

        setData(transformed);
      } catch (error: any) {
        console.error("Failed to fetch employee details:", error);
        toast.error(extractErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    },
    [authHeader]
  );

  const updateProfile = async (
    employee_id: string,
    _token: string | null,
    updatedDetails: EmployeeDetails,
    currentPassword: string,
    newPassword: string
  ) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("firstname", updatedDetails.firstname);
      formData.append("lastname", updatedDetails.lastname);
      formData.append("email", updatedDetails.email);
      formData.append("phone", updatedDetails.phone);
      formData.append("street_address", updatedDetails.street_address);
      formData.append("area", updatedDetails.area);
      formData.append("city_name", updatedDetails.city_name);
      formData.append("state_name", updatedDetails.state_name);
      formData.append("zip_code", updatedDetails.zip_code);
      formData.append("country", updatedDetails.country);

      if (updatedDetails.photo) {
        formData.append("photo", updatedDetails.photo);
      }

      if (newPassword && currentPassword) {
        formData.append("current_password", currentPassword);
        formData.append("new_password", newPassword);
      }

      await axios.put(
        `${BASE_URL}/register/user/profile/${employee_id}`,
        formData,
        {
          headers: {
            ...authHeader,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Profile updated successfully");
    } catch (error: any) {
      console.error("Failed to update profile:", error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const updateAbout = async (
    employee_id: string,
    about: string,
    _token?: string | null
  ) => {
    setIsLoading(true);
    try {
      if (!data) throw new Error("Employee data not available.");

      const formData = new FormData();
      formData.append("about", about || "");

      formData.append("firstname", data.firstname || "");
      formData.append("lastname", data.lastname || "");
      formData.append("email", data.email || "");
      formData.append("phone", data.phone || "");
      formData.append("street_address", data.street_address || "");
      formData.append("area", data.area || "");
      formData.append("city_name", data.city_name || "");
      formData.append("state_name", data.state_name || "");
      formData.append("zip_code", data.zip_code || "");
      formData.append("country", data.country || "");

      if (data.emergency_contact_1) {
        formData.append("emergency_contact_1", data.emergency_contact_1);
      }
      if (data.emergency_contact_2) {
        formData.append("emergency_contact_2", data.emergency_contact_2);
      }

      if (data.photo instanceof File) {
        formData.append("photo", data.photo);
      }

      await axios.put(
        `${BASE_URL}/register/admin/update/${employee_id}`,
        formData,
        {
          headers: {
            ...authHeader,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("About section updated");
    } catch (error: any) {
      console.error("Failed to update 'about' section:", error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const updateEmergencyContacts = async (
    employee_id: string,
    _token: string | null,
    emergency_contact_1: string,
    emergency_contact_2: string,
    existingData: Partial<EmployeeDetails>
  ) => {
    setIsLoading(true);
    try {
      const payload = {
        firstname: existingData.firstname || "",
        lastname: existingData.lastname || "",
        email: existingData.email || "",
        phone: existingData.phone || "",
        street_address: existingData.street_address || "",
        area: existingData.area || "",
        city_name: existingData.city_name || "",
        state_name: existingData.state_name || "",
        zip_code: existingData.zip_code || "",
        country: existingData.country || "",
        about: existingData.about || "",
        emergency_contact_1: Number(emergency_contact_1),
        emergency_contact_2: Number(emergency_contact_2),
      };

      await axios.put(
        `${BASE_URL}/register/admin/update/${employee_id}`,
        payload,
        {
          headers: {
            ...authHeader,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      toast.success("Emergency contacts updated");
    } catch (error: any) {
      console.error("Failed to update emergency contacts:", error);
      toast.error(extractErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    data,
    isLoading,
    fetchDetails,
    updateProfile,
    updateAbout,
    updateEmergencyContacts,
  };
};

export default useEmpDetails;
